import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import ScrambleText from './ScrambleText';
import HeroScene from './HeroScene';
import { useReducedMotion } from '../hooks/useReducedMotion';

const Hero = () => {
  const sectionRef = useRef(null);
  const labelRef = useRef(null);
  const headlineRef = useRef(null);
  const descRef = useRef(null);
  const ctaRef = useRef(null);
  const statsRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(
        labelRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6 }
      )
        .fromTo(
          headlineRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.8 },
          '-=0.3'
        )
        .fromTo(
          descRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.6 },
          '-=0.4'
        )
        .fromTo(
          ctaRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.6 },
          '-=0.3'
        )
        .fromTo(
          statsRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.6 },
          '-=0.2'
        );
    }, sectionRef);

    return () => ctx.revert();
  }, [prefersReducedMotion]);

  return (
    <section
      id="home"
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-background dark:bg-dark-bg"
    >
      {/* Subtle technical grid background */}
      <div className="absolute inset-0 pointer-events-none text-text-primary dark:text-dark-text-primary tech-grid" />

      {/* 3D Canvas — Fullscreen Canvas dengan Z-index di belakang konten */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-0 hidden lg:block">
        <HeroScene />
      </div>

      {/* Hero Content Container */}
      <div className="relative z-10 w-full section-container py-20">
        <div className="max-w-2xl lg:max-w-3xl">
          {/* Technical label */}
          <div ref={labelRef} className="mb-8">
            <span className="tech-label text-accent dark:text-dark-accent">
              IRDO / 2027 · GLOBAL / ROBOTICS
            </span>
          </div>

          {/* Headline */}
          <div ref={headlineRef} className="mb-8">
            <ScrambleText
              text="International Robot Design Olympiad"
              className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.04em] text-text-primary dark:text-dark-text-primary leading-[1.0]"
              scrambleSpeed={55}
              delay={400}
              duration={1800}
              perspective={false}
              hoverEffect={false}
            />
          </div>

          {/* Supporting statement */}
          <p
            ref={descRef}
            className="text-lg sm:text-xl text-text-secondary dark:text-dark-text-secondary max-w-xl mb-10 leading-relaxed"
          >
            An international robotics competition built around engineering,
            creativity, and technology — where precision meets innovation.
          </p>

          {/* CTAs */}
          <div ref={ctaRef} className="flex flex-wrap gap-4">
            <a href="#competition" className="btn-primary">
              Explore Competition
            </a>
            <a href="#register" className="btn-secondary">
              Register
            </a>
          </div>

          {/* Stats */}
          <div
            ref={statsRef}
            className="flex flex-wrap gap-10 sm:gap-16 mt-16 pt-12 border-t border-border dark:border-dark-border"
          >
            {[
              { value: '500+', label: 'Participants' },
              { value: '30+', label: 'Countries' },
              { value: '50+', label: 'Speakers' },
              { value: '$50K', label: 'Prize Pool' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl sm:text-3xl font-semibold text-text-primary dark:text-dark-text-primary font-mono">
                  {stat.value}
                </div>
                <div className="text-sm text-text-muted dark:text-dark-text-muted mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;